import { Atom } from "effect/unstable/reactivity";

import type { EnvironmentId, PhaseEvent } from "@cmmd-forge/contracts";

import { appAtomRegistry } from "~/rpc/atomRegistry";

/**
 * FINDING-CB1: latest server-emitted workspace readiness phase event.
 *
 * Fed from two sources sharing the same per-user PubSub on the server:
 *  - The SSE channel (`GET /api/project/readiness`) before WS upgrade.
 *  - The Effect RPC stream (`subscribeWorkspaceReadiness`) after upgrade.
 *
 * `null` means we haven't received any phase yet — the overlay falls
 * back to its time-derived heuristic so old servers (no readiness
 * channel) and degraded paths (CORS/proxy strip, EventSource blocked)
 * stay no-worse-than-today.
 */
const workspaceReadinessAtom = Atom.make<PhaseEvent | null>(null).pipe(
  Atom.keepAlive,
  Atom.withLabel("workspace-readiness"),
);

export const workspaceReadinessByEnvironmentAtom = Atom.make<
  Partial<Record<EnvironmentId, PhaseEvent>>
>({}).pipe(Atom.keepAlive, Atom.withLabel("workspace-readiness-by-environment"));

/**
 * Monotonic-ts merge to match the server's `WorkspaceReadinessState.update`
 * semantics. If two sources (SSE + RPC) race after handover, we never let
 * the SPA see history reverse — late stale events are dropped.
 */
export function setWorkspaceReadinessPhase(next: PhaseEvent | null): void {
  if (next === null) {
    appAtomRegistry.set(workspaceReadinessAtom, null);
    return;
  }
  const previous = appAtomRegistry.get(workspaceReadinessAtom);
  if (previous !== null && next.ts < previous.ts) return;
  appAtomRegistry.set(workspaceReadinessAtom, next);
}

export function setWorkspaceReadinessPhaseForEnvironment(
  environmentId: EnvironmentId,
  next: PhaseEvent | null,
): void {
  const previousByEnvironment = appAtomRegistry.get(workspaceReadinessByEnvironmentAtom);
  if (next === null) {
    const { [environmentId]: _removed, ...remaining } = previousByEnvironment;
    appAtomRegistry.set(workspaceReadinessByEnvironmentAtom, remaining);
    return;
  }

  const previous = previousByEnvironment[environmentId] ?? null;
  if (previous !== null && next.ts < previous.ts) return;
  appAtomRegistry.set(workspaceReadinessByEnvironmentAtom, {
    ...previousByEnvironment,
    [environmentId]: next,
  });
}

export function getWorkspaceReadinessPhase(): PhaseEvent | null {
  return appAtomRegistry.get(workspaceReadinessAtom);
}

export function getWorkspaceReadinessPhaseForEnvironment(
  environmentId: EnvironmentId | null | undefined,
): PhaseEvent | null {
  if (!environmentId) return null;
  return appAtomRegistry.get(workspaceReadinessByEnvironmentAtom)[environmentId] ?? null;
}

export function resetWorkspaceReadinessPhase(): void {
  appAtomRegistry.set(workspaceReadinessAtom, null);
  appAtomRegistry.set(workspaceReadinessByEnvironmentAtom, {});
}
